import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, X, CornerDownLeft, KeyRound, LogOut, Moon, Sun, RefreshCw, User, Shield, MapPin, UserPlus, Network, ClipboardCheck, FileCheck, ListChecks, Database, Settings2, Plus, Leaf, Zap, TrendingUp, Award, Building2, Calendar, MessageSquare, BarChart3 } from 'lucide-react';
import { useAuth, type AllowedMenu } from '@/contexts/AuthContext';
import { findAllowedMenuLabel } from '@/config/menuInfo';
import { resetMenuTour } from './MenuTour';
import { useReadOnly } from '@/hooks/useReadOnly';

/**
 * Shell-level Ctrl/⌘+K command palette. Indexes every menu the current user
 * is allowed to see (flattened from AuthContext.getAllowedMenus()) so search
 * works from any menu, not just the MFEs (PCF, compliance) that ship their
 * own palette.
 *
 * We deliberately skip mounting the shell palette on pathnames owned by MFEs
 * with their own palette — otherwise both would open on a single Ctrl+K.
 */

type EntryKind = 'menu' | 'action';

interface Entry {
  id: string;
  label: string;
  kind: EntryKind;
  section?: string;
  path?: string;              // present when kind === 'menu'
  run?: () => void;           // present when kind === 'action'
  keywords?: string;          // extra text to match on for actions
  Icon?: React.ComponentType<{ className?: string }>;
}

const MFE_WITH_OWN_PALETTE_PREFIXES = ['/pcf/', '/compliance/'];

// Actions that open a create/edit dialog or change data — not offered to the view-only role.
const WRITE_ACTION_IDS: ReadonlySet<string> = new Set([
  'action:create_role',
  'action:add_location',
  'action:invite_user',
  'action:assign_questions',
  'action:carbon_sync_ce',
  'action:carbon_add_custom_factor',
  'action:carbon_set_frequency',
  'action:carbon_switch_db',
  'action:carbon_scope1',
  'action:carbon_scope2',
  'action:carbon_scope3',
  'action:greencert_add_project',
  'action:greencert_add_membership',
]);

function flatten(menus: AllowedMenu[], section?: string): Entry[] {
  const out: Entry[] = [];
  for (const m of menus) {
    // Show the tenant's own name (Profile → Menu Setup), but keep the original
    // label searchable so people who know the default name still find it.
    const shown = m.customLabel?.trim() || m.label;
    if (m.path) {
      out.push({
        id: m.id, label: shown, kind: 'menu', path: m.path, section,
        ...(shown !== m.label ? { keywords: m.label } : {}),
      });
    }
    if (m.children && m.children.length > 0) {
      out.push(...flatten(m.children, section ?? shown));
    }
  }
  return out;
}

function matches(entry: Entry, q: string): boolean {
  if (!q) return true;
  const hay = `${entry.label} ${entry.section ?? ''} ${entry.path ?? ''} ${entry.keywords ?? ''}`.toLowerCase();
  return q
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .every((tok) => hay.includes(tok));
}

export function CommandPalette(): JSX.Element | null {
  const { getAllowedMenus, logout } = useAuth();
  const readOnly = useReadOnly();
  // Section name for the Framework Questions quick actions = the menu's label
  // as configured in super-admin (static fallback if the menu is not present).
  const frameworkQuestionsLabel = useMemo(
    () => findAllowedMenuLabel(getAllowedMenus() as Array<{ label?: string; path?: string; children?: unknown[] }>, '/data_collection/fillings') || 'Framework Questions',
    [getAllowedMenus],
  );
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const skipOnThisRoute = MFE_WITH_OWN_PALETTE_PREFIXES.some((p) => pathname.startsWith(p));

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);

  // Global actions — appear at the top of an empty search, and are searchable
  // by name or keyword ("password", "logout", "theme", "tour"). New actions:
  // add an entry here; each needs a stable id and a run() callback.
  const actions: Entry[] = useMemo(() => [
    {
      id: 'action:change_password',
      label: 'Change Password',
      kind: 'action',
      section: 'Actions',
      keywords: 'password security account credentials',
      Icon: KeyRound,
      run: () => navigate('/settings/company_profile?action=change_password'),
    },
    {
      id: 'action:view_profile',
      label: 'View My Profile',
      kind: 'action',
      section: 'Actions',
      keywords: 'account personal info me',
      Icon: User,
      run: () => navigate('/settings/company_profile'),
    },
    {
      id: 'action:toggle_theme',
      label: 'Toggle Dark / Light Theme',
      kind: 'action',
      section: 'Actions',
      keywords: 'theme dark light mode appearance',
      Icon: document.documentElement.classList.contains('dark') ? Sun : Moon,
      run: () => {
        const root = document.documentElement;
        const isDark = root.classList.toggle('dark');
        try { localStorage.setItem('theme', isDark ? 'dark' : 'light'); } catch { /* silent */ }
      },
    },
    {
      id: 'action:replay_tour',
      label: 'Replay Tour on This Page',
      kind: 'action',
      section: 'Actions',
      keywords: 'onboarding help walkthrough guide',
      Icon: RefreshCw,
      run: () => resetMenuTour(window.location.pathname),
    },
    {
      id: 'action:logout',
      label: 'Log Out',
      kind: 'action',
      section: 'Actions',
      keywords: 'signout sign out exit quit',
      Icon: LogOut,
      run: () => logout(),
    },
    // Org Setup quick actions — navigate to the page with an ?action= hint
    // that Team.tsx reads to auto-switch tab + open the matching modal.
    {
      id: 'action:create_role',
      label: 'Create Role',
      kind: 'action',
      section: 'Org Setup',
      keywords: 'role permission access rbac new',
      Icon: Shield,
      run: () => navigate('/settings/org_setup?action=create_role'),
    },
    {
      id: 'action:add_location',
      label: 'Add Facility',
      kind: 'action',
      section: 'Org Setup',
      keywords: 'facility site office plant facility address',
      Icon: MapPin,
      run: () => navigate('/settings/org_setup?action=add_location'),
    },
    {
      id: 'action:invite_user',
      label: 'Invite User',
      kind: 'action',
      section: 'Org Setup',
      keywords: 'invite user member team email onboard',
      Icon: UserPlus,
      run: () => navigate('/settings/org_setup?action=invite_user'),
    },
    {
      id: 'action:setup_org_chart',
      label: 'Setup Org Chart',
      kind: 'action',
      section: 'Org Setup',
      keywords: 'org chart hierarchy reporting structure',
      Icon: Network,
      run: () => navigate('/settings/org_setup?action=setup_org_chart'),
    },
    // Framework Questions quick actions — each navigates to the page with a
    // ?action= hint that FrameworkQuestions.tsx reads on mount.
    {
      id: 'action:assign_questions',
      label: 'Assign Questions',
      kind: 'action',
      section: frameworkQuestionsLabel,
      keywords: 'assign kpi question owner delegate distribute framework',
      Icon: ClipboardCheck,
      run: () => navigate('/data_collection/fillings?action=assign_questions'),
    },
    {
      id: 'action:my_assignments',
      label: 'My Assignments',
      kind: 'action',
      section: frameworkQuestionsLabel,
      keywords: 'my assignments mine queue tasks todo pending',
      Icon: FileCheck,
      run: () => navigate('/data_collection/fillings?action=my_assignments'),
    },
    {
      id: 'action:data_collection',
      label: 'Open Data Collection',
      kind: 'action',
      section: frameworkQuestionsLabel,
      keywords: 'data collection framework tree all questions brsr gri csrd',
      Icon: ListChecks,
      run: () => navigate('/data_collection/fillings?action=data_collection'),
    },
    {
      id: 'action:search_questions',
      label: 'Search Questions',
      kind: 'action',
      section: frameworkQuestionsLabel,
      keywords: 'search find topic kpi question filter',
      Icon: Search,
      run: () => navigate('/data_collection/fillings?action=search'),
    },
    // Carbon Emissions quick actions — each navigates to the page with an
    // ?action= hint that CarbonEmissions.tsx reads on mount.
    {
      id: 'action:carbon_sync_ce',
      label: 'Sync CE Values',
      kind: 'action',
      section: 'GHG Emissions',
      keywords: 'sync recalculate ce carbon emission formula refresh',
      Icon: RefreshCw,
      run: () => navigate('/data_collection/carbon_emission?action=sync_ce'),
    },
    {
      id: 'action:carbon_add_custom_factor',
      label: 'Add Custom Emission Factor',
      kind: 'action',
      section: 'GHG Emissions',
      keywords: 'custom factor emission add new bespoke tenant',
      Icon: Plus,
      run: () => navigate('/data_collection/carbon_emission?action=add_custom_factor'),
    },
    {
      id: 'action:carbon_set_frequency',
      label: 'Set Reporting Frequency',
      kind: 'action',
      section: 'GHG Emissions',
      keywords: 'frequency monthly quarterly annual cadence reporting',
      Icon: Settings2,
      run: () => navigate('/data_collection/carbon_emission?action=set_frequency'),
    },
    {
      id: 'action:carbon_switch_db',
      label: 'Switch GHG Database',
      kind: 'action',
      section: 'GHG Emissions',
      keywords: 'database defra epa ipcc factor switch change source',
      Icon: Database,
      run: () => navigate('/data_collection/carbon_emission?action=switch_database'),
    },
    {
      id: 'action:carbon_scope1',
      label: 'Add Scope 1 Emission',
      kind: 'action',
      section: 'GHG Emissions',
      keywords: 'scope 1 stationary mobile fugitive biogenic combustion direct',
      Icon: Leaf,
      run: () => navigate('/data_collection/carbon_emission?action=scope1'),
    },
    {
      id: 'action:carbon_scope2',
      label: 'Add Scope 2 Emission',
      kind: 'action',
      section: 'GHG Emissions',
      keywords: 'scope 2 electricity purchased energy indirect',
      Icon: Zap,
      run: () => navigate('/data_collection/carbon_emission?action=scope2'),
    },
    {
      id: 'action:carbon_scope3',
      label: 'Add Scope 3 Emission',
      kind: 'action',
      section: 'GHG Emissions',
      keywords: 'scope 3 value chain supplier travel commute upstream downstream',
      Icon: TrendingUp,
      run: () => navigate('/data_collection/carbon_emission?action=scope3'),
    },
    // Green Certification quick actions — each navigates to the page with an
    // ?action= hint that GreenCertification.tsx reads on mount.
    {
      id: 'action:greencert_add_project',
      label: 'Add Green Cert Project',
      kind: 'action',
      section: 'Green Certification',
      keywords: 'green certification project igbc leed griha rating create new building',
      Icon: Award,
      run: () => navigate('/green_certification?action=add_project'),
    },
    {
      id: 'action:greencert_add_membership',
      label: 'Add Certification Membership',
      kind: 'action',
      section: 'Green Certification',
      keywords: 'membership igbc usgbc leed griha register body',
      Icon: Building2,
      run: () => navigate('/green_certification?action=add_membership'),
    },
    {
      id: 'action:greencert_dashboard',
      label: 'Green Cert Dashboard',
      kind: 'action',
      section: 'Green Certification',
      keywords: 'green certification dashboard portfolio overview kpis',
      Icon: BarChart3,
      run: () => navigate('/green_certification?action=view_dashboard'),
    },
    {
      id: 'action:greencert_projects',
      label: 'View Green Cert Projects',
      kind: 'action',
      section: 'Green Certification',
      keywords: 'projects list building certification portfolio',
      Icon: ListChecks,
      run: () => navigate('/green_certification?action=view_projects'),
    },
    {
      id: 'action:greencert_meetings',
      label: 'Green Cert Meetings & Actions',
      kind: 'action',
      section: 'Green Certification',
      keywords: 'meetings actions certification body review schedule',
      Icon: Calendar,
      run: () => navigate('/green_certification?action=view_meetings'),
    },
    {
      id: 'action:greencert_queries',
      label: 'Green Cert Queries',
      kind: 'action',
      section: 'Green Certification',
      keywords: 'queries questions reviewer thread response',
      Icon: MessageSquare,
      run: () => navigate('/green_certification?action=view_queries'),
    },
    // Water & Effluent Compliance quick actions
    {
      id: 'action:water_setup',
      label: 'Water Site Setup',
      kind: 'action',
      section: 'Water Compliance',
      keywords: 'water effluent site setup facility cpcb schedule vi outlet',
      Icon: Building2,
      run: () => navigate('/water/setup'),
    },
    {
      id: 'action:water_consents',
      label: 'Water Consents & NOCs',
      kind: 'action',
      section: 'Water Compliance',
      keywords: 'consent cte cto cgwa noc ulb permit renewal',
      Icon: ClipboardCheck,
      run: () => navigate('/water/consents'),
    },
    {
      id: 'action:water_events',
      label: 'Water Events & Risk',
      kind: 'action',
      section: 'Water Compliance',
      keywords: 'water events breach ocems offline risk ngt cgwa non-compliance',
      Icon: MessageSquare,
      run: () => navigate('/water/events'),
    },
    // Waste & Compliance quick actions
    {
      id: 'action:waste_dashboard',
      label: 'Waste Dashboard',
      kind: 'action',
      section: 'Waste & Compliance',
      keywords: 'waste dashboard hazardous plastic ewaste bmw compliance',
      Icon: BarChart3,
      run: () => navigate('/waste'),
    },
    {
      id: 'action:waste_streams',
      label: 'Waste Streams',
      kind: 'action',
      section: 'Waste & Compliance',
      keywords: 'waste streams hazardous hw plastic pw ewaste bmw category',
      Icon: ListChecks,
      run: () => navigate('/waste/streams'),
    },
    {
      id: 'action:waste_consents',
      label: 'Consents & Authorizations',
      kind: 'action',
      section: 'Waste & Compliance',
      keywords: 'cte cto consent authorization hw epr registration spcb',
      Icon: Shield,
      run: () => navigate('/waste/consents'),
    },
    {
      id: 'action:waste_vendors',
      label: 'Waste Vendors',
      kind: 'action',
      section: 'Waste & Compliance',
      keywords: 'vendor tsdf cbwtf recycler transporter authorised',
      Icon: Building2,
      run: () => navigate('/waste/vendors'),
    },
    {
      id: 'action:waste_manifests',
      label: 'Waste Manifests',
      kind: 'action',
      section: 'Waste & Compliance',
      keywords: 'manifest form10 challan dispatch chain custody hazardous',
      Icon: FileCheck,
      run: () => navigate('/waste/manifests'),
    },
  ], [logout, navigate]);

  const menuEntries = useMemo(() => flatten(getAllowedMenus()), [getAllowedMenus]);
  const results = useMemo(() => {
    // With no query: actions first, then menus. With a query: rank actions
    // and menu items equally by string match, actions still shown first for
    // ties so common tasks stay reachable in 1–2 keystrokes.
    const filteredActions = actions.filter((a) => !(readOnly && WRITE_ACTION_IDS.has(a.id)) && matches(a, query));
    const filteredMenus = menuEntries.filter((e) => matches(e, query));
    return [...filteredActions, ...filteredMenus].slice(0, 20);
  }, [actions, menuEntries, query, readOnly]);

  // Ctrl/⌘+K opens; Esc closes. On skip-routes we listen but do nothing.
  useEffect(() => {
    if (skipOnThisRoute) return;
    const onKey = (e: KeyboardEvent) => {
      const isOpener = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if (isOpener) {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery('');
        setHighlight(0);
      } else if (e.key === 'Escape' && open) {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, skipOnThisRoute]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  if (skipOnThisRoute || !open) return null;

  const pick = (entry: Entry): void => {
    setOpen(false);
    if (entry.kind === 'action' && entry.run) {
      entry.run();
    } else if (entry.path) {
      navigate(entry.path);
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Command palette"
      className="fixed inset-0 z-[1100] flex items-start justify-center pt-[10vh] px-4 bg-slate-900/40 backdrop-blur-sm"
      onClick={() => setOpen(false)}
    >
      <div
        className="w-full max-w-xl rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-3 border-b border-slate-200 dark:border-slate-700">
          <Search className="w-4 h-4 text-slate-400 flex-shrink-0" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search menus or actions…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') {
                e.preventDefault();
                setHighlight((h) => Math.min(results.length - 1, h + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setHighlight((h) => Math.max(0, h - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                const chosen = results[highlight];
                if (chosen) pick(chosen);
              }
            }}
            className="flex-1 py-3 bg-transparent text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none"
          />
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <ul className="max-h-[50vh] overflow-y-auto py-1">
          {results.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-slate-500">No matches</li>
          ) : (
            results.map((r, i) => {
              const Icon = r.Icon;
              const isAction = r.kind === 'action';
              return (
                <li key={`${r.id}:${r.path ?? 'action'}`}>
                  <button
                    type="button"
                    onClick={() => pick(r)}
                    onMouseEnter={() => setHighlight(i)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${
                      i === highlight
                        ? 'bg-blue-50 text-blue-900 dark:bg-blue-900/30 dark:text-blue-100'
                        : 'text-slate-700 dark:text-slate-200'
                    }`}
                  >
                    {Icon && (
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isAction ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`} />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="truncate flex items-center gap-2">
                        {r.label}
                        {isAction && (
                          <span className="text-[9px] uppercase tracking-wide bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-200 px-1.5 py-0.5 rounded">
                            Action
                          </span>
                        )}
                      </div>
                      {(r.section || r.path) && (
                        <div className="text-[10px] text-slate-400 truncate">
                          {r.section}{r.section && r.path ? ' · ' : ''}{r.path ?? ''}
                        </div>
                      )}
                    </div>
                    {i === highlight && (
                      <CornerDownLeft className="w-3 h-3 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                </li>
              );
            })
          )}
        </ul>
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 dark:border-slate-700 text-[10px] text-slate-400">
          <span>↑↓ navigate · ⏎ open · Esc close</span>
          <span>{results.length} result{results.length === 1 ? '' : 's'}</span>
        </div>
      </div>
    </div>
  );
}

export default CommandPalette;
